import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO, { SEO_DATA } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Check, ArrowRight, Phone, MessageCircle, HeadphonesIcon, Star } from 'lucide-react';

export default function CustomerServicePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...SEO_DATA.customerService} />
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">Specialized Help</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Resume Help for Customer Service Jobs
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              Customer service professionals need resumes that showcase communication skills, problem-solving ability, and a track record of customer satisfaction.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* Common Problems */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Common Resume Problems for Customer Service Roles
          </h2>
          
          <div className="space-y-4">
            {[
              { problem: 'Generic job duties instead of achievements', fix: 'We quantify your impact—customer satisfaction scores, resolution rates, retention improvements.' },
              { problem: 'Missing soft skills that employers value', fix: 'We highlight communication, empathy, patience, and conflict resolution in natural ways.' },
              { problem: 'Outdated formatting that doesn\'t pass ATS', fix: 'Clean, ATS-friendly format that gets through screening systems.' },
              { problem: 'No differentiation from other candidates', fix: 'We position your unique strengths and the value you bring to the role.' },
            ].map((item, i) => (
              <Card key={i} className="p-6 rounded-2xl border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-red-600 font-bold">✕</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 mb-1">{item.problem}</p>
                    <p className="text-slate-600 text-sm">→ {item.fix}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* What Gets Improved */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Outfit' }}>
              What We Improve for Customer Service Resumes
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Phone, title: 'Call Center Experience', desc: 'Highlight call volume, handle time, and quality scores effectively.' },
                { icon: MessageCircle, title: 'Chat & Email Support', desc: 'Showcase multi-channel communication and written skills.' },
                { icon: HeadphonesIcon, title: 'Technical Support', desc: 'Balance technical knowledge with customer-facing skills.' },
                { icon: Star, title: 'Customer Satisfaction', desc: 'Quantify CSAT, NPS, or satisfaction metrics from your roles.' },
                { icon: Check, title: 'Soft Skills', desc: 'Communicate empathy, patience, and problem-solving naturally.' },
                { icon: Check, title: 'Career Progression', desc: 'Show growth from representative to lead to supervisor roles.' },
              ].map((item, i) => (
                <Card key={i} className="p-6 rounded-2xl border border-slate-200">
                  <item.icon className="h-8 w-8 text-teal-600 mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Roles We Help With */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Customer Service Roles We Help With
          </h2>
          
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'Customer Service Representative',
              'Call Center Agent',
              'Technical Support Specialist',
              'Help Desk Analyst',
              'Customer Success Manager',
              'Client Support Specialist',
              'Chat Support Agent',
              'Email Support Representative',
              'Customer Experience Coordinator',
              'Support Team Lead',
              'Customer Service Supervisor',
              'Retail Customer Service',
            ].map((role, i) => (
              <Badge key={i} className="bg-slate-100 text-slate-700 px-4 py-2 text-sm">{role}</Badge>
            ))}
          </div>
        </section>

        {/* Process */}
        <section className="bg-slate-100 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              How It Works
            </h2>
            
            <div className="space-y-4">
              {[
                { step: 1, title: 'Submit Your Intake', desc: 'Upload your current resume and tell us about your customer service background and goals.' },
                { step: 2, title: 'We Review & Confirm Scope', desc: 'We review your materials, identify improvements, and confirm pricing before invoicing.' },
                { step: 3, title: 'Payment & Work Begins', desc: 'After payment, we rewrite your resume to highlight your customer service strengths.' },
                { step: 4, title: 'Delivery & Revisions', desc: 'You receive your polished resume with reasonable revisions included within scope.' },
              ].map((item) => (
                <div key={item.step} className="flex gap-4 items-start bg-white p-4 rounded-xl">
                  <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Pages */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Explore More
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/for/office-admin" className="text-teal-700 hover:text-teal-800 underline">Office & Admin Jobs</Link>
            <span className="text-slate-300">|</span>
            <Link to="/for/career-change" className="text-teal-700 hover:text-teal-800 underline">Career Changers</Link>
            <span className="text-slate-300">|</span>
            <Link to="/services/resume-rewrite" className="text-teal-700 hover:text-teal-800 underline">Resume Rewrite Service</Link>
            <span className="text-slate-300">|</span>
            <Link to="/pricing" className="text-teal-700 hover:text-teal-800 underline">View Pricing</Link>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Card className="p-8 rounded-3xl bg-slate-900 text-white">
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              Ready to improve your customer service resume?
            </h3>
            <p className="text-slate-300 mb-6">
              Submit your intake and we'll review your resume personally. No payment until scope is confirmed.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}
